import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { sendOtp, verifyOtp } from "../services/auth";
import { Button } from "../components/utils/Button";
import { Typography } from "../components/utils/Typography";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^\d{7,15}$/;

const isValidIdentifier = (value) =>
  EMAIL_REGEX.test(value) || MOBILE_REGEX.test(value);

export default function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSendOtp = async (e) => {
    e.preventDefault();
    const value = identifier.trim();
    if (!value) return;
    if (!isValidIdentifier(value)) {
      setError("Please enter a valid email address or phone number.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await sendOtp(value);
      setStep(2);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Failed to send OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await verifyOtp(identifier.trim(), otp.trim());
      const token = res.data.data?.jwt_token;
      if (!token) throw new Error("No token in response");
      localStorage.setItem("token", token);
      const user = { email: res.data.data?.user_id };
      localStorage.setItem("user", JSON.stringify(user));
      navigate("/select-company");
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Invalid OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    /* Full-screen white page */
    <div className="min-h-screen w-full bg-white flex flex-col md:flex-row">
      {/* Left — dark rounded card with watermark background (desktop only) */}
      <div className="hidden md:flex shrink-0 p-4 xl:p-8" style={{ width: "58%" }}>
        <div
          className="w-full h-full rounded-2xl flex flex-col justify-center px-16 relative overflow-hidden"
          style={{
            minHeight: 0,
            backgroundColor: "#191A20",
            backgroundImage: "url(/loginArt.svg)",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "contain",
          }}
        >
          <h1 className="text-white font-sora font-extralight text-[clamp(1rem,2.2vw,2.5rem)] leading-[1.5] tracking-[2px] align-middle">
            <span className="whitespace-nowrap block">
              Data shared on your terms.
            </span>
            <span className="whitespace-nowrap block">
              Manage it the same way.
            </span>
          </h1>
          <p className="text-gray-300 mt-4 lg:mt-6 font-sora font-light text-[clamp(0.7rem,1vw,1rem)] leading-[1.8] tracking-[0.8px] align-middle max-w-[50ch]">
            Manage your consents, correct your information and raise concerns, Simply and Securely.
          </p>

          <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center gap-2 text-white text-sm font-serif">
            <span>Powered by</span>
            <img
              src="/assets/nyai-full-white.svg"
              alt="NYAI"
              width={46}
              height={12}
            />
          </div>
        </div>
      </div>

      {/* Mobile logo */}
      <div className="flex md:hidden justify-center pt-24 pb-16">
        <img src="/assets/logo.svg" alt="NYAI" width={120} height={31} />
      </div>

      {/* Right — form panel, plain white, vertically centered */}
      <div className="flex flex-col items-center md:justify-center flex-1 px-6 xl:px-16 py-4 md:py-16">
        <div className="w-full md:mx-0" style={{ maxWidth: 480 }}>
          <Typography
            variant="h1"
            as="h2"
            className="md:text-4xl md:font-bold mb-3 font-serif md:font-sans"
          >
            Sign In
          </Typography>
          <Typography
            variant="body"
            color="muted"
            className="md:text-base mb-10 leading-relaxed"
          >
            Enter your registered email or phone number{" "}
            <span className="hidden md:inline">
              to continue to your consent dashboard.
            </span>
          </Typography>

          {error && (
            <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm whitespace-pre-line">
              {error}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleSendOtp} className="space-y-10">
              <div>
                <Typography variant="label" htmlFor="identifier" className="mb-2">
                  Email ID/ Phone Number
                </Typography>
                <input
                  id="identifier"
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="Enter your email id or phone number"
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ "--tw-ring-color": "#2d7a7a" }}
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !isValidIdentifier(identifier.trim())}
                className="w-full !py-3.5 !text-base"
                style={{ background: "#2d7a7a" }}
              >
                {loading ? "Sending..." : "Send Verification Code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-10">
              <div>
                <Typography variant="label" htmlFor="identifier-readonly" className="mb-2">
                  Email ID/ Phone Number
                </Typography>
                <input
                  id="identifier-readonly"
                  type="text"
                  value={identifier}
                  disabled
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-base bg-gray-50 text-gray-400"
                />
              </div>
              <div>
                <Typography variant="label" htmlFor="otp" className="mb-2">
                  Verification Code
                </Typography>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="Enter your verification code"
                  required
                  autoFocus
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ "--tw-ring-color": "#2d7a7a" }}
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !otp.trim()}
                className="w-full !py-3.5 !text-base"
                style={{ background: "#2d7a7a" }}
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
              <Button
                type="button"
                variant="tertiary"
                onClick={() => {
                  setStep(1);
                  setOtp("");
                  setError("");
                }}
                className="w-full !justify-center !p-0 !text-gray-400 hover:!text-gray-600 hover:!bg-transparent md:!inline-flex hidden"
              >
                ← Change email/phone number
              </Button>
              <Button
                type="button"
                variant="tertiary"
                onClick={() => {
                  setStep(1);
                  setOtp("");
                  setError("");
                }}
                className="w-full !justify-center !p-0 !text-[#3F707D] !font-semibold hover:!bg-transparent md:hidden"
              >
                Back
              </Button>
            </form>
          )}
        </div>
      </div>

      {/* Mobile footer */}
      <div className="flex md:hidden items-center justify-center gap-2 py-6 text-sm text-black font-serif">
        <span>Powered by</span>
        <img src="/assets/logo.svg" alt="NYAI" width={46} height={12} />
      </div>
    </div>
  );
}
